import axios from 'axios';

const SUPPORTED_LOCALES = ['es', 'en', 'el']
const browserPrimary = (navigator.language || 'es').split('-')[0].toLowerCase()
const initialLocale = SUPPORTED_LOCALES.includes(browserPrimary) ? browserPrimary : 'en'

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '', // En producción suele ser relativo
    withCredentials: true, // Importante para Sanctum (cookies)
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'Accept-Language': initialLocale,
    },
});



// URLs que no deben disparar el interceptor de 401
// (rutas de autenticación donde un 401 es esperado)
const AUTH_URLS = [
    '/api/v1/auth/login',
    '/api/v1/auth/user',
    '/api/v1/auth/logout',
    '/sanctum/csrf-cookie',
];

// Estado para evitar múltiples redirecciones simultáneas
let isRedirectingToLogin = false;

// Importamos de forma lazy para evitar dependencias circulares
let toastFunction = null;
let userRef = null;
let routerInstance = null;

const getRouter = async () => {
    if (!routerInstance) {
        const router = await import('@/router');
        routerInstance = router.default;
    }
    return routerInstance;
};

const getToast = async () => {
    if (!toastFunction) {
        const { useToast } = await import('@/hooks/use-toast');
        const { toast } = useToast();
        toastFunction = toast;
    }
    return toastFunction;
};

const getUserRef = async () => {
    if (!userRef) {
        const { useAuth } = await import('@/hooks/useAuth');
        const { user } = useAuth();
        userRef = user;
    }
    return userRef;
};

// Interceptor de respuesta para manejar errores globales
api.interceptors.response.use(
    // Respuestas exitosas pasan sin cambios
    (response) => response,

    // Manejo de errores
    async (error) => {
        // Forms opting in own their error presentation (including auth 403s).
        if (error.config?.skipGlobalErrorHandler) return Promise.reject(error);

        const status = error.response?.status;
        const requestUrl = error.config?.url || '';

        // Verificar si es una URL de auth (donde 401 es esperado)
        const isAuthUrl = AUTH_URLS.some(url => requestUrl.includes(url));

        // 401 Unauthorized - Sesión expirada
        if (status === 401 && !isAuthUrl && !isRedirectingToLogin) {
            isRedirectingToLogin = true;

            try {
                // Limpiar estado del usuario
                const user = await getUserRef();
                user.value = null;

                // Mostrar toast de sesión expirada
                const toast = await getToast();
                toast({
                    title: 'Sesión expirada',
                    description: 'Tu sesión ha caducado. Por favor, inicia sesión de nuevo.',
                    variant: 'destructive',
                    duration: 5000,
                });

                // Redirigir al login
                const router = await getRouter();
                await router.push('/login');
            } finally {
                // Reset del flag después de un breve delay para evitar múltiples triggers
                setTimeout(() => {
                    isRedirectingToLogin = false;
                }, 1000);
            }
        }

        // 429 Too Many Requests - Rate limiting
        if (status === 429) {
            const toast = await getToast();
            const retryAfter = error.response?.headers?.['retry-after'];
            const waitTime = retryAfter ? `${retryAfter} segundos` : 'unos minutos';

            toast({
                title: 'Demasiadas solicitudes',
                description: `Has excedido el límite de solicitudes. Por favor, espera ${waitTime} antes de intentarlo de nuevo.`,
                variant: 'destructive',
                duration: 6000,
            });
        }

        // 403 Forbidden - Sin permisos
        if (status === 403) {
            const errorCode = error.response?.data?.error?.code;
            const toast = await getToast();

            if (errorCode === 'SUBSCRIPTION_REQUIRED') {
                toast({
                    title: 'Suscripción requerida',
                    description: 'Necesitas una suscripción activa para realizar esta acción.',
                    variant: 'destructive',
                    duration: 5000,
                });
            } else {
                toast({
                    title: 'Acceso denegado',
                    description: 'No tienes permisos para realizar esta acción.',
                    variant: 'destructive',
                    duration: 4000,
                });
            }
        }

        // 500+ Server errors
        if (status >= 500) {
            const toast = await getToast();
            toast({
                title: 'Error del servidor',
                description: 'Ha ocurrido un error en el servidor. Por favor, inténtalo más tarde.',
                variant: 'destructive',
                duration: 4000,
            });
        }

        // Propagar el error para que los componentes puedan manejarlo también
        return Promise.reject(error);
    }
);

export default api;
